import { ChangeEvent, useCallback, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import debounce from 'lodash/debounce'
import {
  ArrowUp,
  CirclePlus,
  Component as ComponentIcon,
  Download as ExportIcon,
  Zap,
} from 'lucide-react'
import { useHistory } from 'react-router-dom'
import { AdminHeader } from '@components/layout/admin-header'
import { Toolbar } from '@components/layout/toolbar'
import { Button } from '@components/v2/button'
import { Input } from '@components/v2/input'
import { Modal } from '@components/v2/modal'
import { api } from 'api'
import { DottedGrid } from './components/dotted-grid'
import { t } from '@lingui/core/macro'

export const ListsManagerNew = () => {
  const history = useHistory()

  const [state, setState] = useState({
    name: '',
    description: '',
    listType: '',
  })

  const { name, description, listType } = state

  const debounceSetName = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      setState(prev => ({ ...prev, name: e.target.value }))
    }, 300),
    [],
  )

  const debounceSetDescription = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      setState(prev => ({ ...prev, description: e.target.value }))
    }, 300),
    [],
  )

  interface CreateListParams {
    id: number | null
    name: string
    notes: string
    type: string
  }
  const { mutate: createList, isPending: isLoading } = useMutation<
    { data: { id: number } },
    Error,
    CreateListParams
  >({
    mutationFn: newList => api.put('/api/v1/account/lists/', newList),
    onSuccess: ({ data }) => {
      history.replace(`/admin/lists-manager/edit/${data.id}`)
    },
  })

  return (
    <Page>
      <AdminHeader>
        <AdminHeader.PageIcon>
          <ComponentIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.ManagerTitle>{t`List manager`}</AdminHeader.ManagerTitle>
          <AdminHeader.Title>{t`Create list`}</AdminHeader.Title>
        </div>
      </AdminHeader>
      <Toolbar>
        <Toolbar.Search callback={null} onKeyPress={null} onChange={null} />
        <Toolbar.Left>
          <Button disabled variant="secondary" className="flex gap-2 items-center mr-4">
            <ArrowUp size={21} />
            {t`Import`}
          </Button>
          <Button
            disabled
            variant="secondary"
            className="flex gap-2 items-center mr-3 bg-[#E3EAEC] border-cv-blue-300 text-[#8E9EA5]"
          >
            <ExportIcon size={21} />
            {t`Export`}
          </Button>
          <Button
            disabled
            variant="secondary"
            className="flex gap-2 items-center bg-[#E3EAEC] border-cv-blue-300 text-[#8E9EA5]"
          >
            <Zap size={21} />
            {t`Power with dataset`}
          </Button>
        </Toolbar.Left>
        <Toolbar.Right>
          <Button variant="primary">{t`Save`}</Button>
        </Toolbar.Right>
      </Toolbar>

      <DottedGrid>
        <Button
          variant="secondary"
          className="flex gap-2 items-center m-8 border-dashed !text-black"
        >
          <CirclePlus size={21} color="#a9c5d0" />
          {t`Add new column`}
        </Button>
        <Modal.Container isOpen={true} className="max-w-[471px] pt-10 pb-8">
          <h2 className="text-[18px] font-bold mb-9">{t`Create a new list`}</h2>
          <Input
            label={t`Name`}
            placeholder={t`Name`}
            id="name"
            message={t`We recommend a name that has all important identifiable details.`}
            className="max-w-[350px] w-full"
            onChange={debounceSetName}
          />

          <Input
            label={t`Description`}
            placeholder={t`Describe the use-cases for this template.`}
            id="description"
            type="textarea"
            rows={4}
            className="w-full"
            onChange={debounceSetDescription}
          />

          <div className="border border-cv-blue-300 rounded-md inline-block">
            <select
              className="text-cv-blue-900 text-[12px] py-2 pl-3 pr-8 border-r-[12px] border-transparent rounded-md"
              value={listType}
              onChange={e => {
                setState(prev => ({ ...prev, listType: e.target.value }))
              }}
            >
              {[
                { label: t`List Type`, value: '' },
                { label: t`Drop Down`, value: 'drop-down' },
                { label: t`Drill Down`, value: 'drill-down' },
                { label: t`Dependent`, value: 'dependent' },
              ].map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <Modal.Footer>
            <Button
              variant="dark"
              loadingText={t`Creating list...`}
              type="submit"
              isLoading={isLoading}
              onClick={() => {
                createList({
                  id: null,
                  name,
                  notes: description,
                  type: listType,
                })
              }}
            >
              {t`Save`}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                history.replace(`/admin/lists-manager`)
              }}
            >
              {t`Cancel`}
            </Button>
          </Modal.Footer>
        </Modal.Container>
      </DottedGrid>
    </Page>
  )
}

function Page({ children }) {
  return (
    <motion.div
      className="new-list h-full bg-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      {children}
    </motion.div>
  )
}
